import http from '../http'

/**
 * 创建台账（仅创建台账头）
 * @param {Object} data - 台账数据
 * @param {Number} data.contractId - 关联的合同ID
 * @param {String} [data.dueDateBasis] - 应付日期时间依据：SETTLEMENT_TIME（结算时间）、SHIPMENT_TIME（发货时间）、RECEIPT_TIME（收货时间）；不传默认 SETTLEMENT_TIME
 * @param {String} [data.remark] - 备注
 * @returns {Promise} 返回创建的台账信息
 */
export function create_fund_ledger(data) {
  const requestData = {
    contractId: data.contractId
  }
  if (data.dueDateBasis) {
    requestData.dueDateBasis = data.dueDateBasis
  }
  if (data.remark) {
    requestData.remark = data.remark
  }
  return http.post('/fund-ledgers', requestData)
}

/**
 * 更新台账（仅更新台账头）
 * @param {Number} id - 台账ID
 * @param {Object} data - 台账数据（所有字段都是可选的，只更新提供的字段）
 * @param {Number} [data.contractId] - 关联的合同ID
 * @param {String} [data.dueDateBasis] - 应付日期时间依据：SETTLEMENT_TIME、SHIPMENT_TIME、RECEIPT_TIME
 * @param {String} [data.remark] - 备注
 * @returns {Promise} 返回更新后的台账信息
 */
export function update_fund_ledger(id, data) {
const requestData = {}
  
  if (data.contractId !== undefined) {
    requestData.contractId = data.contractId
  }
  if (data.dueDateBasis !== undefined) {
    requestData.dueDateBasis = data.dueDateBasis
  }
  if (data.remark !== undefined) {
    requestData.remark = data.remark
  }
  
  return http.put(`/fund-ledgers/${id}`, requestData)
}

/**
 * 根据ID查询台账
 * @param {Number} id - 台账ID
 * @returns {Promise} 返回台账详情，包括台账基本信息和子项列表
 */
export function query_fund_ledger_by_id(id) {
return http.get(`/fund-ledgers/${id}`)
}

/**
 * 分页查询台账列表
 * @param {Object} params - 查询参数
 * @param {Number} [params.contractId] - 合同ID
 * @param {String} [params.startDate] - 开始日期，格式：yyyy-MM-dd HH:mm:ss
 * @param {String} [params.endDate] - 结束日期，格式：yyyy-MM-dd HH:mm:ss
 * @param {String} [params.ledgerNumber] - 台账编号（支持模糊查询）
 * @param {String} [params.contractName] - 合同名称（支持模糊查询）
 * @param {String} [params.demandUnitName] - 需求方名称（支持模糊查询）
 * @param {String} [params.remark] - 台账备注（支持模糊查询）
 * @param {Number} [params.page] - 页码，从0开始，默认0
 * @param {Number} [params.size] - 每页数量，默认10
 * @param {String} [params.sortBy] - 排序字段，默认createTime
 * @param {String} [params.sortDir] - 排序方向，可选值：asc、desc，默认desc
 * @returns {Promise} 返回台账列表和分页信息
 */
export function query_fund_ledgers_pagination(params = {}) {
// 构建查询参数
  const queryParams = {
    page: params.page !== undefined ? params.page : 0,
    size: params.size || 10,
    sortBy: params.sortBy || 'createTime',
    sortDir: params.sortDir || 'desc'
  }
  
  // 添加可选查询参数（台账不再包含资金类型，列表不按 fundType 筛选）
  if (params.contractId) {
    queryParams.contractId = params.contractId
  }
  if (params.startDate) {
    queryParams.startDate = params.startDate
  }
  if (params.endDate) {
    queryParams.endDate = params.endDate
  }
  if (params.ledgerNumber) {
    queryParams.ledgerNumber = params.ledgerNumber
  }
  if (params.contractName) {
    queryParams.contractName = params.contractName
  }
  if (params.demandUnitName) {
    queryParams.demandUnitName = params.demandUnitName
  }
  if (params.remark) {
    queryParams.remark = params.remark
  }
  
  return http.get('/fund-ledgers', { params: queryParams })
}

/**
 * 删除台账
 * @param {Number} id - 台账ID
 * @returns {Promise} 返回删除结果
 */
export function delete_fund_ledger(id) {
return http.delete(`/fund-ledgers/${id}`)
}

/**
 * 添加收付款记录（多选结算单 + 本笔总金额，后端按时间依据自动分摊到各结算单流水）
 * @param {Number} ledgerId - 台账ID
 * @param {Object} data - 收付款记录数据
 * @param {String} data.fundType - 资金类型：RECEIVABLE（收款）/ PAYABLE（付款）
 * @param {Number} data.amount - 本笔收/付款总金额（须 ≤ 所选结算单金额之和）
 * @param {String} data.paymentDate - 收/付款时间，格式：yyyy-MM-dd HH:mm:ss
 * @param {Array<Number>} data.settlementIds - 选中的结算单ID列表，按结算时间升序自动分摊
 * @param {String} [data.paymentMethod] - 支付方式
 * @param {Array<String>} [data.attachmentPaths] - 附件路径列表
 * @param {Array<String>} [data.attachmentNames] - 附件名称列表（与attachmentPaths一一对应）
 * @param {String} [data.remark] - 备注
 * @returns {Promise} 返回创建的收付款记录信息（含 ledgerFlows）
 */
export function add_payment_record(ledgerId, data) {
  const requestData = {
    fundType: data.fundType,
    amount: data.amount,
    paymentDate: data.paymentDate,
    settlementIds: data.settlementIds || []
  }
  if (data.paymentMethod) {
    requestData.paymentMethod = data.paymentMethod
  }
  if (data.attachmentPaths && data.attachmentPaths.length > 0) {
    requestData.attachmentPaths = data.attachmentPaths
  }
  if (data.attachmentNames && data.attachmentNames.length > 0) {
    requestData.attachmentNames = data.attachmentNames
  }
  if (data.remark) {
    requestData.remark = data.remark
  }
  return http.post(`/fund-ledgers/${ledgerId}/payment-records`, requestData)
}

/**
 * 更新收付款记录（仅允许更新与分摊无关的字段：付款时间、支付方式、附件、备注）
 * @param {Number} recordId - 收付款记录ID
 * @param {Object} data - 可更新字段
 * @param {String} [data.paymentDate] - 收/付款时间
 * @param {String} [data.paymentMethod] - 支付方式
 * @param {Array<String>} [data.attachmentPaths] - 附件路径列表
 * @param {Array<String>} [data.attachmentNames] - 附件名称列表
 * @param {String} [data.remark] - 备注
 * @returns {Promise} 返回更新后的收付款记录信息（含 ledgerFlows）
 */
export function update_payment_record(recordId, data) {
  const requestData = {}
  if (data.paymentDate !== undefined) requestData.paymentDate = data.paymentDate
  if (data.paymentMethod !== undefined) requestData.paymentMethod = data.paymentMethod
  if (data.attachmentPaths !== undefined) requestData.attachmentPaths = data.attachmentPaths
  if (data.attachmentNames !== undefined) requestData.attachmentNames = data.attachmentNames
  if (data.remark !== undefined) requestData.remark = data.remark
  return http.put(`/fund-ledgers/payment-records/${recordId}`, requestData)
}

/**
 * 删除收付款记录
 * @param {Number} recordId - 收付款记录ID
 * @returns {Promise} 返回删除结果
 */
export function delete_payment_record(recordId) {
  return http.delete(`/fund-ledgers/payment-records/${recordId}`)
}

/**
 * 根据台账ID查询合同相关结算单
 * @param {Number} ledgerId - 台账ID
 * @returns {Promise} 返回该台账对应合同下的结算单列表（去重）
 */
export function query_settlements_by_ledger_id(ledgerId) {
  return http.get(`/fund-ledgers/${ledgerId}/settlements`)
}

/**
 * 根据单位ID查询合同及结算单列表
 * @param {Number} unitId - 单位ID
 * @returns {Promise} 返回该单位相关的所有合同及每个合同关联的结算单列表
 */
export function query_contracts_by_unit(unitId) {
return http.get(`/fund-ledgers/contracts-by-unit/${unitId}`)
}

export default {
  create_fund_ledger,
  update_fund_ledger,
  query_fund_ledger_by_id,
  query_fund_ledgers_pagination,
  delete_fund_ledger,
  add_payment_record,
  update_payment_record,
  delete_payment_record,
  query_settlements_by_ledger_id,
  query_contracts_by_unit
}

